/**
 * Enhanced fetch client with global 401 error handling
 * Automatically redirects to login page when receiving unauthorized responses
 */

interface FetchOptions extends RequestInit {
  skipAuthRedirect?: boolean;
}

/**
 * Wrapper around fetch that handles 401 responses globally
 * @param url - The URL to fetch
 * @param options - Fetch options (headers, method, body, etc.)
 * @returns Promise with the response
 */
export async function fetchClient(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { skipAuthRedirect, ...fetchOptions } = options;

  try {
    const response = await fetch(url, fetchOptions);

    // Handle 401 Unauthorized errors globally
    if (response.status === 401 && !skipAuthRedirect) {
      // Clear auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Redirect to login page
      window.location.href = '/login';

      // Return the response anyway for any cleanup logic
      return response;
    }

    return response;
  } catch (error) {
    // Re-throw network errors
    throw error;
  }
}

/**
 * Convenience wrapper for JSON API calls with automatic 401 handling
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @returns Promise with parsed JSON data
 */
export async function fetchJSON<T = any>(
  url: string,
  options: FetchOptions = {}
): Promise<T> {
  const response = await fetchClient(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response.json();
}
